// The one piece of state plumbing the UI has.
//
// The world is redrawn sixty times a second; the HUD changes a handful of
// times a second. Re-rendering from inside `requestAnimationFrame` is the
// obvious way to join the two and it is the wrong one — it hands Preact sixty
// diffs a second to throw away, on a page whose whole job is to stay out of the
// game's frame budget.
//
// So the loop pushes a whole model every frame and this store decides whether
// anything actually moved. A frame that changed no number on screen notifies
// nobody and the DOM is not touched. That is the entire contract between the
// game and the UI, and it is why there is no state library here.

import { useEffect, useState } from "preact/hooks";

export interface Store<T> {
  get(): T;
  /** Replace the value. Subscribers hear about it only if it differs. */
  set(next: T): void;
  /** Replace some fields. Same rule. */
  patch(part: Partial<T>): void;
  subscribe(listener: () => void): () => void;
}

/** Equality over one level of own keys. The models here are flat records of
 *  primitives plus one array that is replaced rather than mutated, so this is
 *  the whole of "did anything change". */
export function shallowEqual<T extends object>(a: T, b: T): boolean {
  if (a === b) return true;
  const keys = Object.keys(a) as Array<keyof T>;
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => Object.is(a[key], b[key]));
}

export function createStore<T extends object>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      if (shallowEqual(value, next)) return;
      value = next;
      for (const listener of [...listeners]) listener();
    },
    patch(part) {
      this.set({ ...value, ...part });
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** Read a store from a component.
 *
 *  The re-read on mount is not belt and braces: a snapshot can land between the
 *  render that read the value and the effect that subscribes, and without it
 *  that update is lost until the next one happens to arrive. */
export function useStore<T extends object>(store: Store<T>): T {
  const [value, setValue] = useState(store.get);
  useEffect(() => {
    const sync = (): void => setValue(store.get());
    sync();
    return store.subscribe(sync);
  }, [store]);
  return value;
}
